import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { cloneDeep } from "lodash";
import { ErrorBanner } from "rhombus-react";
import {
	ADTYPE_LABELS,
	AE_SUPPORTED_AD_TYPES,
	MIN_CAROUSEL_CARDS_BY_ADTYPE,
	DEFAULT_MIN_CAROUSEL_CARDS
} from "social/constants/common";
import { CTAS_BY_ADTYPE } from "social/constants/CTA";
import {
	titleCase,
	isImposiumCreative,
	isAfterEffectsCreative,
	isDynamicRenderSocial,
	isAdstudioCreative
} from "utils/common";
import {
	API_URL, CREATIVE_ASSET_TYPES,
	PLATFORM_BY_ADTYPE,
	SOCIAL_AD_TYPES,
	UPLOAD_IMAGE_ASSET_MAX_BYTES,
	UPLOAD_VIDEO_ASSET_MAX_BYTES
} from "utils/constants";
import { getErrorMessagesByAdType } from "selectors/Social2";


export function adTypeSort(typeA, typeB){
	if(typeB.platform !== typeA.platform){
		return typeA.platform.localeCompare(typeB.platform);
	} else {
		return typeA.adType.localeCompare(typeB.adType);
	}
}

export function adTypeLabelSort(typeA, typeB){
	/*
		Sorts adTypes alphabetically by their displaying labels, not their adType codes.
	*/
	if(typeB.platform !== typeA.platform){
		// Platforms are already added as labels
		return typeA.platform.localeCompare(typeB.platform);
	} else {
		return (ADTYPE_LABELS[typeA.adType] || '').localeCompare(ADTYPE_LABELS[typeB.adType] || '');
	}
}

export function normalizeCardAPIWarnings(warnings = [], cards = []){
	/*
		warnings is expected to be an array of objects in the form:
		```
		[
			{
				// Item has no `id` property if it's a carousel-level problem
				// i.e. not card-specific, but related to the carousel's cards
				size: [
					"Carousel requires at least 2 cards"
				]
			},
			{
				id: 2650 // The card ID
				image: [
					"Image is required."
				],
				landingPage: [
					"Invalid format for landing page. URL format is required.",
					"Max landing page length is 800 characters."
				],
			},
			...
		]
		```
		`cards` is the `socialValues.cards` data from the Creative, which we need to be able
		to reference the card IDs and get their order, which we'll use as an index.

		We want to return an object in the form:
		{
			cards: {
				"0": { // `id` is removed, and this index is the `order` for the card with the matching id
					image: [
						"Image is required.",
					],
					landingPage: [
						"Invalid format for landing page. URL format is required.",
						"Max landing page length is 800 characters.",
					]
				},
			},
			"Cards size": [
				"Carousel requires at least 2 cards",
			],
		}
	*/
	if(warnings.length){
		const extraWarnings = {};
		const normalized = warnings.reduce((prev, cardWarnings) => {
			const { id } = cardWarnings;

			if(!id){
				// This is warnings for the whole carousel, rather than a specific card
				Object.entries(cardWarnings).forEach(([key, value]) => {
					extraWarnings[`Cards ${key}`] = value;
				});
			} else {
				const order = cards.find(card => card.id === id)?.order;
				if(order !== undefined){ // We test for undefined because order might be 0 (i.e. Card 1)
					const idStripped = { ...cardWarnings };
					delete idStripped.id;
					prev[order] = idStripped;
				}
			}

			return prev;
		}, {});

		return {
			...extraWarnings,
			cards: normalized,
		};
	}
}

export function adTypePrettyName(adType) {
	return `${PLATFORM_BY_ADTYPE[adType]} ${ADTYPE_LABELS[adType]}`;
}

export function prettifyCardWarnings(warnings){
	/*
		`warnings` comes in as an object, with numbered keys corresponding to the `card.order`.
		It's basically an array, but you can't use Array methods on it, and it may be sparsely populated.

		e.g.
			{
				"0": {
					"landingPage": "Field is required"
				},
				"2": {
					"image": "Field is required"
				}
			}

		We convert this to an array of strings like:
		"Card 1 - Landing Page - Field is required" // Cards are indexed by 0 internally, but named starting at 1.
		"Card 3 - Image - Field is required"
	*/
	const messages = [];
	Object.entries(warnings).map(([cardOrder, cardWarnings]) => {
		Object.entries(cardWarnings).forEach(([field, fieldWarning]) => {
			/*
				fieldWarning may be a string (when it comes from redux-form validation),
				or an array-like object (when it comes from the API), so we just normalize to an array of strings
			*/
			let warningStrings = (typeof(fieldWarning) === "object") ? Object.values(fieldWarning) : [fieldWarning];
			warningStrings.forEach((warning) => {
				messages.push(`Card ${Number(cardOrder)+1} - ${titleCase(field)} - ${warning}`);
			});
		});
	});

	return messages;
}

const isObjectWithIntegerKeys = obj => (
	Object.keys(obj).every(key => Number.isInteger(Number(key)))
);

export function prettifyMessages(messageMap) {
	return Object.keys(messageMap).reduce((prev, key) => {
		const messageData = messageMap[key];

		if(key === "cards"){
			prev.push(...prettifyCardWarnings(messageData));
			return prev;
		}

		const label = titleCase(key);
		const prefix = `${label} - `;

		// For API errors, we will receive an array of messages and we want to show them all.
		if (Array.isArray(messageData)) {
			messageData.forEach((mes, index) => {
				if (typeof mes === "object" && mes !== null) {
					Object.entries(mes).forEach(([field, fieldMessage]) => {
						if (fieldMessage) {
							prev.push(`${label} ${index + 1} - ${titleCase(field)} - ${fieldMessage}`);
						}
					});
				} else {
					prev.push(`${prefix}${mes}`);
				}
			});
		} else if (typeof messageData === "object") {
			// Redux converts some arrays into objects with integer keys. Handle this case separately.
			if (isObjectWithIntegerKeys(messageData)) {
				Object.entries(messageData).forEach(([index, mes]) => {
					if (typeof mes === "object" && mes !== null) {
						Object.entries(mes).forEach(([field, fieldMessage]) => {
							if (fieldMessage) {
								prev.push(`${label} ${Number(index) + 1} - ${titleCase(field)} - ${fieldMessage}`);
							}
						});
					} else {
						prev.push(`${prefix}${mes}`);
					}
				});
			} else {
				Object.entries(messageData).forEach(([key, mes]) => {
					if (typeof mes === "object" && mes !== null) {
						Object.entries(mes).forEach(([field, fieldMessage]) => {
							if (fieldMessage) {
								prev.push(`${label} - ${titleCase(key)} ${titleCase(field)} - ${fieldMessage}`);
							}
						});
					} else {
						prev.push(`${titleCase(label)} ${titleCase(key)} - ${mes}`);
					}
				});
			}
		} else {
			prev.push(`${prefix}${messageData}`);
		}

		return prev;
	}, []);
}

export function formatAPIOption(option){
	/*
		When we get Creatives from the API, anything that comes from a Select will usually be
		in the form { id, name } instead of { value, label }, which we use with Select inputs.

		This function will format the API value to fill in `value` and `label` with what the
		UI expects, if they're missing, and will also fallback to using the `id` property for both
		if nothing better is available.
	*/

	return {
		...option,
		label: option?.label || option?.name || option?.username || option?.id,
		value: option?.value || option?.id,
	};
}

export function normalizeAPIOption(option){
	/*
		Our form inputs use options in { value, label } format, but the API generally expects
		them as { id, name }, so we store them with those properties for easier serialization.

		In the event that we do not have an option, we return null to indicate that nothing was set.
	*/

	if (!option) {
		return null;
	}

	return {
		...option,
		id: option.value,
		name: option.label,
	};
}

export function mapStringToOption(value){
	return {
		value,
		label: value,
	};
}

export function normalizeOptionToString(option){
	return option?.value || null;
}

export function mapFacebookStringToOption(value) {
	return {
		value,
		id: value,
		label: value,
	};
}

export function dynamicMapStringToOption(value){
	return {
		...value,
		value: mapStringToOption(value?.value),
	};
}

export function dynamicNormalizeOptionToString(option){
	return {
		...option,
		value: normalizeOptionToString(option?.value),
	};
}

/**
 * Some fields (checkbox, SponsorPageId) on Creatives are treated as dynamic, but aren't
 * We can normalize and format the values so that redux-form saves things correctly without us needing complicated maps
 */
export function nonDynamicFields(){
	return {
		format(value){
			return value?.value;
		},
		normalize(value){
			return {
				isDynamic:false,
				dynVarName:"",
				value,
			};
		},
	};
}

/*
	CTA Utils
*/
export function intersection(lists){
	/*
		This computes the Set intersection of an array of arrays of items.
	*/
	if(lists.length === 0){
		return [];
	}
	if(lists.length === 1){
		return lists[0];
	}

	let [listA, listB, ...rest] = lists;

	if(lists.length === 2){ // eslint-disable-line no-magic-numbers
		/*
			Iterate over the shorter list, as it will be a strict superset of the intersection
		*/
		let [shortest, longestSet] = (listA.length < listB.length) ? [listA, new Set(listB)] : [listB, new Set(listA)];
		let intersectionSet = new Set();

		for(let i=0; i<shortest.length; i++){
			let item = shortest[i];
			if(longestSet.has(item)){
				intersectionSet.add(item);
			}
		}

		return [...intersectionSet];
	}

	/*
		If there are more than 2 lists, compute the intersection of the first two,
		and recursively compute that list against the rest
	*/
	return intersection([intersection([listA, listB]), ...rest]);
}

export function getSharedCTAs(adTypes, extraCTASByAdType = {}){
	/*
		This returns the Set intersection of the corresponding CTA lists for each adType,
		as option objects to be used in the Shared Content CTA dropdown.

		The function will preferentially use our static hash of values,
		but you can pass in extraCTASByAdType, which will be referenced as a backup.

		It should be an object in the form:
		{
			[SOCIAL_AD_TYPES.WHATEVER_TYPE]: ["CTA1", "CTA2", ...],
			...
		}

		This allows for dynamic lists of CTAs from an API, etc.
	*/
	let listSet = adTypes.reduce((prev, adType) => {
		// Some adTypes will have the same CTA list reference, so we use a Set to minimize what we have to iterate over
		let ctas = CTAS_BY_ADTYPE[adType] || extraCTASByAdType[adType];
		if(ctas){
			prev.add(ctas);
		}
		return prev;
	}, new Set());

	let values = intersection([...listSet]);
	return values.map(mapStringToOption);
}

export function useErrorBanner(adType, showPristineErrors, fieldConfig){
	const errorMessages = useSelector(getErrorMessagesByAdType(adType, showPristineErrors));
	const prettyErrorMessages = useMemo(() => (
		prettifyMessages(errorMessages, fieldConfig)
	), [errorMessages]);
	const hasErrors = prettyErrorMessages.length;


	if (hasErrors) {
		return <ErrorBanner title="Sorry, please fix the errors below" messages={prettyErrorMessages} />;
	}

	return null;
}

export function getCardIndexFromName(name){
	/*
		This takes a form field name like "facebookCarouselMO.cards[0].landingPage", and
		extracts a 1-based card index, or "N" if no index is found.

		We need this for generating default dynamic variable names like "card1_clickTag1"
	*/
	const zeroIndexMatch = name.match(/cards\[(\d+)\]/)?.[1];
	if(zeroIndexMatch){
		return `${Number(zeroIndexMatch) + 1}`;
	}
	return "N";
}


export function getPlacementPrefix(fieldName){
	/*
		Placement field names are formatted like "facebookImageMO.placements.Square.image",
		while the default field names are formatted like "facebookImageMO.image".

		If the name is formatted like a placement, we extract the placement name and return it
		lowercased, with an underscore.  Otherwise, we return an empty string.
	*/
	const namePattern = /placements\.(\w+)\./;
	const matches = fieldName.match(namePattern);
	return matches ? `${matches[1]}_`.toLowerCase() : "";
}

export function nameWithPlacementPrefix(defaultName){
	/*
		This returns a function that can be given to a Field for defaultDynVarName to generate
		a name with an appropriate placement prefix if needed.

		If the corresponding Creative field has a dynVarName set, its value will be used for the
		suffix, otherwise the defaultName will be used.

		e.g. "square_image" for a Square placement, or just "image" for the Default placement.
	*/
	return ({ name }, creativeValues = {}) => {
		const prefix = getPlacementPrefix(name);

		const nameTokens = name.split(".");
		const fieldName = nameTokens[nameTokens.length - 1];

		const defaultPlacementValue = creativeValues[fieldName]?.dynVarName;

		const suffix = defaultPlacementValue || defaultName;

		return `${prefix}${suffix}`;
	};
}

export function getFlatSocialVals(socialValues = {}) {
	let flatSocVals = {};
	Object.entries(socialValues).forEach((v) => {
		const [key, value] = v;
		if(/cards/i.test(key)){
			flatSocVals[key] = value?.map((card) => {
				return getFlatSocialVals(card);
			});
		} else {
			// discovery here reffers to the key within `socialValues`
			// Only snapStoryCarousel creatives currently have this object
			if(/discovery/.test(key)){
				flatSocVals[key] = getFlatSocialVals(value);
				// TODO: remove once rhombus 2.9.10 is up
				if(flatSocVals.discovery?.backgroundImage){
					if(flatSocVals.discovery.backgroundImage.src){
						flatSocVals.discovery.backgroundImage.src = `${API_URL}${flatSocVals.discovery.backgroundImage?.src}`;
					}
				}
				if(flatSocVals.discovery?.logoImage){
					if(flatSocVals.discovery.logoImage?.src){
						flatSocVals.discovery.logoImage.src = `${API_URL}${flatSocVals.discovery.logoImage?.src}`;
					}
				}
			} else if (key === "disclaimer" && value && typeof value === "object") {
				Object.assign(flatSocVals, value);
			} else if(/image|video/i.test(key)) {
				flatSocVals[key] = value;
			} else {
				flatSocVals[key] = value?.hasOwnProperty("value") ? value?.value : value;
			}
		}
	});
	return flatSocVals;
}

const nonObjCtaTypes = [
	SOCIAL_AD_TYPES.LINKEDIN_VIDEO,
	SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL,
	SOCIAL_AD_TYPES.SNAPCHAT_IMAGE,
	SOCIAL_AD_TYPES.SNAPCHAT_COMMERCIAL,
	SOCIAL_AD_TYPES.SNAPCHAT_VIDEO,
	SOCIAL_AD_TYPES.TIKTOK_VIDEO,
];

export function getSocialValuesFromVariables(socialValues = {}, variables = {}, libraryId, adType){
	let currentSocial = {};
	Object.entries(socialValues).forEach((entries) => {
		let [key, value] = entries
			, currentVar = null
			;

		if(value === null){
			return;
		}

		if(key === "cards"){
			currentSocial[key] = value.map((card) => {
				return {
					...getSocialValuesFromVariables(card, variables, libraryId),
					//type and order are necessary for carousel previews
					type: card.type,
					order: card.order,
					id: card.id,
				};
			});
			return;
		}

		if(key === "placements") {
			/** This map needs to be separate from 'cards' map because we
			 * need to spread the whole object, which breaks carousels
			 */
			currentSocial[key] = value.map((placement) => {
				return {
					...getSocialValuesFromVariables(placement, variables, libraryId),
					...placement,
				};
			});
			return;
		}

		if(value?.dynVarName){
			currentVar = variables[value.dynVarName];
		}

		if(!value.isDynamic){
			//for disclaimer, we only want this logic if key is exactly equal to disclaimer. not for props that container disclaimer like `disclaimerText`
			if(/image|video|thumb/i.test(key) || key === 'disclaimer') {
				currentSocial[key] = {
					...value,
				};
			} else {
				currentSocial[key] = value.value;
			}
		} else if(currentVar){
			if(/text/i.test(currentVar.type)){
				currentSocial[key] = currentVar.data;

				//Not supported right now, but might as well add it for future
				if(currentVar.assetId && !currentVar.isFormatted){
					currentSocial[key] = currentVar.prop;
				}

				if(/cta/i.test(key)) {
					/** any adType ouside this array expects
					 * an object with cta.name for rendering the
					 * adType
					 */
					if(!nonObjCtaTypes.includes(adType)) {
						currentSocial[key] = {
							...currentVar,
							name: currentVar.data,
						};
					}
				}
			}
			if(/image/i.test(currentVar.type)){
				let imgSrc;
				if(currentVar.prop) {
					//redux form value
					let [, src] = currentVar.prop.split(".net");
					imgSrc = src;
				} else {
					imgSrc = `/crm/v1/asset-manager/${libraryId}/images/instantAssets/${currentVar.data}`;
				}
				currentSocial[key] = {
					id:currentVar.assetId,
					name:currentVar.name,
					src: imgSrc,
				};
			}

			if(/video/i.test(currentVar.type)){
				currentSocial[key] = {
					id:currentVar.assetId,
					name:currentVar.name,
					src:`/crm/v1/asset-manager/${libraryId}/videos/${currentVar.data}`,
				};
			}
		} else {
			if(value?.value) {
				currentSocial[key] = value.value;
			}
		}
	});

	return {
		...currentSocial,
		placementAssetCustomizationType: socialValues?.placementAssetCustomizationType,
	};
}

export const appendCacheBustToUrl = (url, cacheBust) => {
	if(!cacheBust) {
		return url;
	}
	const i = url.indexOf("?");
	const path = i === -1 ? url : url.slice(0, i);
	const queryString = i === -1 ? "" : url.slice(i + 1);
	const params = new URLSearchParams(queryString);
	params.set("cb", cacheBust);
	return `${path}?${params.toString()}`;
};

export function cacheBustAsset(asset, cacheBust){
	if(!asset?.src || !cacheBust){ return asset; }
	return { ...asset, src: appendCacheBustToUrl(asset.src, cacheBust) };
}

export function cacheBustRenderAssets(socialValues, cacheBust){
	if(!socialValues || !cacheBust){ return socialValues; }

	const busted = { ...socialValues };

	if(busted.video?.src){
		busted.video = cacheBustAsset(busted.video, cacheBust);
	}
	if(busted.image?.src){
		busted.image = cacheBustAsset(busted.image, cacheBust);
	}
	if(busted.thumbnail?.src){
		busted.thumbnail = cacheBustAsset(busted.thumbnail, cacheBust);
	}
	if(Array.isArray(busted.cards)){
		busted.cards = busted.cards.map(card => cacheBustRenderAssets(card, cacheBust));
	}
	if(Array.isArray(busted.placements)){
		busted.placements = busted.placements.map(placement => cacheBustRenderAssets(placement, cacheBust));
	}

	return busted;
}

const getVersionRenderUrl = (src, creative, versionId, arrayItemId, cacheBust) => {
	let srcParts = src?.split("/");
	if(!srcParts) {
		return src;
	}
	let filenameIndex = srcParts.findIndex(part => part.includes("."));
	if(filenameIndex === -1) {
		return src;
	}
	//keep the extension from the base creative's socialValues
	let ext = srcParts[filenameIndex].split(".").pop();

	let pacOrCardId = arrayItemId ? `-${arrayItemId}` : "";
	if(isImposiumCreative(creative)) {
		srcParts[filenameIndex] = `imposium-render-${creative.id}${pacOrCardId}-${versionId}.${ext}`;
	} else if(isAfterEffectsCreative(creative)) {
		srcParts[filenameIndex] = `after-effects-render-${creative.id}${pacOrCardId}-${versionId}.${ext}`;
	} else if(isAdstudioCreative(creative)) {
		if(ext === 'jpeg') {
			ext = 'png';
		}
		srcParts[filenameIndex] = `adstudio-render-${creative.id}${pacOrCardId}-${versionId}.${ext}`;

		if(ext === 'mp4' || ext === 'mov') {
			srcParts.splice(filenameIndex - 1, 1);
		} else {
			srcParts[filenameIndex - 1] = `${creative.id}`;
		}
	}

	return appendCacheBustToUrl(srcParts.join("/"), cacheBust);
};

const setVersionRenderAssetUrls = (creative, versionId, socialValues, arrayItemId, cacheBust) => {
	let socialValuesClone = cloneDeep(socialValues);
	if(socialValuesClone?.image?.src) {
		socialValuesClone.image.src = getVersionRenderUrl(socialValuesClone.image.src, creative, versionId, arrayItemId, cacheBust);
	}
	if(socialValuesClone?.thumbnail?.src) {
		socialValuesClone.thumbnail.src = getVersionRenderUrl(socialValuesClone.thumbnail.src, creative, versionId, arrayItemId, cacheBust);
	}
	if(socialValuesClone?.video?.src) {
		socialValuesClone.video.src = getVersionRenderUrl(socialValuesClone.video.src, creative, versionId, arrayItemId, cacheBust);
	}
	return socialValuesClone;
};

export const setDynamicRenderUrls = (creative, versionId, socialValues, cacheBust) => {
	if(!creative || !versionId || !isDynamicRenderSocial(creative)) {
		return socialValues;
	}

	let socialValuesClone = cloneDeep(socialValues);
	if(socialValuesClone?.placements) {
		socialValuesClone.placements = socialValuesClone.placements.map((placement) => {
			return setVersionRenderAssetUrls(creative, versionId, placement, placement?.type?.id, cacheBust);
		});

		//special case for the base level asset for pac creatives, give it a placementId of 1
		const DEFAULT_PLACEMENT_ID = 1;
		return setVersionRenderAssetUrls(creative, versionId, socialValuesClone, DEFAULT_PLACEMENT_ID, cacheBust);
	}
	if(socialValuesClone?.cards) {
		socialValuesClone.cards = socialValuesClone.cards.map((card) => {
			return setVersionRenderAssetUrls(creative, versionId, card, card?.id, cacheBust);
		});
	}

	return setVersionRenderAssetUrls(creative, versionId, socialValuesClone, null, cacheBust);
};

export function getMinNumCards(adType){
	return MIN_CAROUSEL_CARDS_BY_ADTYPE[adType] ?? DEFAULT_MIN_CAROUSEL_CARDS;
}

/*
	Sometimes our asset sizes are limited by a platform's restrictions, and sometimes
	the limit is our infrastructure.  These functions allow us to use the effective limit
	without having to touch every field when the restrictions on either change.
*/
export function videoMaxBytes(platformLimitBytes){
	if(platformLimitBytes === undefined){
		return UPLOAD_VIDEO_ASSET_MAX_BYTES;
	}
	return Math.min(platformLimitBytes, UPLOAD_VIDEO_ASSET_MAX_BYTES);
}

export function imageMaxBytes(platformLimitBytes){
	if(platformLimitBytes === undefined){
		return UPLOAD_IMAGE_ASSET_MAX_BYTES;
	}
	return Math.min(platformLimitBytes, UPLOAD_IMAGE_ASSET_MAX_BYTES);
}

export function isRequiredAssetField(assetType){
	return !assetType || assetType === CREATIVE_ASSET_TYPES.PREBUILT;
}

export function isBuildMethodSupported(assetType, { adType, isDynamic } = {}) {
	switch (assetType) {
	case CREATIVE_ASSET_TYPES.IMPOSIUM:
		return !!isDynamic;
	case CREATIVE_ASSET_TYPES.AFTER_EFFECTS:
		return !!isDynamic && AE_SUPPORTED_AD_TYPES.includes(adType);
	default:
		return true;
	}
}
